'use client'

import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import FieldDropBar from '@/components/onboarding/FieldDropBar'
import { SkillSelector } from '@/components/onboarding/SkillSelector'
import {
  designLevelOptions,
  engagementTypeOptions,
  materialsOptions,
  weeklyHoursOptions,
  workTypeOptions,
} from '../constants'

const FUNCTIONALITY_PLACEHOLDER = `목표(무엇을 달성?)
포함 범위(반드시 포함할 것)
제외 범위(하지 않을 것)
레퍼런스/선호 스타일(링크)
완료 기준(언제 "완료"로 볼지)`

interface WorkScopeStepProps {
  formData: {
    functionality: string
    designLevel: string
    mainCategories: string[]
    subCategories: string[]
    skills: string[]
    workType: string
    materials: string[]
    referenceService: string
    engagementType: string
    weeklyHours: string
    otherRequirements: string
  }
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrevious: () => void
}

export const WorkScopeStep: React.FC<WorkScopeStepProps> = ({ formData, onChange }) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const requiresWeeklyHours = ['ONGOING', 'ONE_OFF_EXTENDABLE'].includes(formData.engagementType)

  return (
    <div className="space-y-8">
      {/* 상단 헤더 */}
      <div className="space-y-1">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">작업 범위</h2>
        <p className="text-sm text-gray-600">무엇을, 어디까지, 어떤 기준으로 완성할지 정합니다.</p>
        <p className="text-xs text-blue-600">팁: 범위를 명확히 적을수록 견적 비교가 쉬워집니다.</p>
      </div>

      <div className="space-y-6">
        {/* 섹션 1) 핵심 요구사항 */}
        <div className="space-y-2">
          <Label htmlFor="functionality">상세 브리프 <span className="text-blue-600">*</span></Label>
          <Textarea
            id="functionality"
            value={formData.functionality}
            onChange={(e) => onChange('functionality', e.target.value)}
            maxLength={2000}
            className="min-h-[160px]"
            placeholder={FUNCTIONALITY_PLACEHOLDER}
          />
        </div>

        {/* 섹션 2) 결과물/옵션 */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="referenceService">레퍼런스 링크 <span className="text-xs text-gray-500">(선택)</span></Label>
            <Input
              id="referenceService"
              type="text"
              value={formData.referenceService}
              onChange={(e) => onChange('referenceService', e.target.value)}
              placeholder="참고 서비스 또는 URL"
            />
          </div>
          <div className="space-y-2">
            <Label>이미 보유한 자료 <span className="text-xs text-gray-500">(선택)</span></Label>
            <div className="flex flex-wrap gap-2">
              {materialsOptions.map((item) => {
                const selected = formData.materials.includes(item.value)
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() =>
                      onChange(
                        'materials',
                        selected ? formData.materials.filter((v) => v !== item.value) : [...formData.materials, item.value]
                      )
                    }
                    className={`rounded-full border px-3 py-1 text-xs ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700'}`}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* 섹션 3) 진행 방식/조건 */}
        <div className="space-y-4 rounded-xl border border-gray-200 p-4">
          <div className="space-y-2">
            <Label>진행 방식 <span className="text-xs text-gray-500">(선택)</span></Label>
            <div className="flex flex-wrap gap-2">
              {workTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange('workType', option.value)}
                  className={`rounded-full border px-3 py-1 text-xs ${formData.workType === option.value ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>요구되는 숙련도 <span className="text-blue-600">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {designLevelOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange('designLevel', option.value)}
                  className={`rounded-full border px-3 py-1 text-xs ${formData.designLevel === option.value ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>진행 형태 <span className="text-blue-600">*</span></Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {engagementTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onChange('engagementType', option.value)}
                  className={`rounded-xl border px-3 py-2 text-left text-sm ${formData.engagementType === option.value ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {requiresWeeklyHours && (
            <div className="space-y-2">
              <Label htmlFor="weeklyHours">주간 예상 투입(지속형일 때만) <span className="text-blue-600">*</span></Label>
              <select
                id="weeklyHours"
                value={formData.weeklyHours}
                onChange={(e) => onChange('weeklyHours', e.target.value)}
                className="w-full h-11 rounded-xl border border-gray-200 px-4 text-sm"
              >
                <option value="">선택해주세요</option>
                {weeklyHoursOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* 섹션 4) 카테고리/스킬 (고급 옵션) */}
        <div className="space-y-2 rounded-xl border border-gray-200 p-3">
          <button
            type="button"
            onClick={() => setIsAdvancedOpen((prev) => !prev)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="text-sm font-medium text-gray-700">분야 태그 / 키워드 <span className="text-gray-500">(고급 옵션)</span></span>
            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`} />
          </button>
          {isAdvancedOpen && (
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">분야 태그</Label>
                <FieldDropBar
                  value={{ mainCategories: formData.mainCategories, subCategories: formData.subCategories }}
                  onChange={(v) => {
                    onChange('mainCategories', v.mainCategories)
                    onChange('subCategories', v.subCategories)
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-600">키워드(툴/스킬/업무 태그)</Label>
                <SkillSelector
                  value={formData.skills}
                  onChange={(skills) => onChange('skills', skills)}
                  placeholder="키워드 입력"
                  showSelected={false}
                />
              </div>
            </div>
          )}
        </div>

        {/* 섹션 5) 기타 요구사항 */}
        <div className="space-y-2">
          <Label htmlFor="otherRequirements">추가 요청사항 <span className="text-xs text-gray-500">(선택)</span></Label>
          <Textarea
            id="otherRequirements"
            value={formData.otherRequirements}
            onChange={(e) => onChange('otherRequirements', e.target.value)}
            className="min-h-[100px]"
            placeholder="협업 툴, 커뮤니케이션 방식, 일정 제약 등이 있으면 적어주세요."
          />
        </div>
      </div>
    </div>
  )
}
